/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { classNames } from '../utils/UNSAFE_classNames';
import { Keys, containsKey } from '../utils/UNSAFE_keys';
import { useSelection } from '../hooks/PRIVATE_useSelection';
import { SelectionDetail } from '../UNSAFE_Collection';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { CheckboxOffIcon } from '../PRIVATE_ThemedIcons/CheckboxOffIcon';
import { CheckboxOnIcon } from '../PRIVATE_ThemedIcons/CheckboxOnIcon';
import { CheckboxMixedIcon } from '../PRIVATE_ThemedIcons/CheckboxMixedIcon';
import { Flex } from '../UNSAFE_Flex';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';
import { multiVariantStyles, styles } from './themes/SelectorStyles.css';
import { RadioStyles } from '../UNSAFE_Radio/themes/RadioStyles.css';
import { RadioIconVariantOptions } from '../UNSAFE_Radio/themes/RadioIconStyles.css';
import { RadioIconRedwoodTheme } from '../UNSAFE_Radio/themes/redwood/RadioIconTheme';
/**
 * Props for the Selector Component
 */
export type Props<K> = {
  /**
   * The key associated with the Selector.
   */
  rowKey: K;
  /**
   * The selected keys.
   */
  selectedKeys: Keys<K>;
  /**
   * aria-label for this selector
   */
  'aria-label'?: string;
  /**
   * Determine whether to show the visual partial icon when selector is checked. If the
   * selector is checked and isPartial is set to true, the selector would show a partially
   * checked icon.
   */
  isPartial?: boolean;
  /**
   * Specifies the selection mode. For selection mode 'all', please refer to SelectorAll component.
   */
  selectionMode?: 'multiple' | 'single';
  /**
   * Callback function to invoke when the selected keys has changed when
   * the checkbox is toggled.
   */
  onChange?: (detail: SelectionDetail<K>) => void;
};

// internal component to use in place of the real Checkbox
export function Checkbox(props: {
  checked: boolean;
  'aria-label'?: string;
  isPartial?: boolean;
  onClick?: (event: MouseEvent) => void;
}) {
  const { isTabbable } = useTabbableMode();

  const { interactionProps, applyActiveStyle } = useInteractionStyle();

  // because Checkbox components reference Radio scoped vars, we need to bring in the Radio base theme
  // to pick up the scoped var definitions.
  const { classes: radioClassess } = useComponentTheme<RadioIconVariantOptions, RadioStyles>(
    RadioIconRedwoodTheme,
    { iconSize: '4xUnits' }
  );
  const variantClasses = multiVariantStyles({
    checked: props.checked ? (props.isPartial ? 'isPartiallyChecked' : 'isChecked') : 'notChecked',
    active: applyActiveStyle ? 'isActive' : 'notActive'
  });
  const classes = classNames([radioClassess, styles.checkbox, variantClasses]);

  const ariaChecked = props.checked ? (props.isPartial ? 'mixed' : 'true') : 'false';

  return (
    <div
      {...interactionProps}
      class={classes}
      tabIndex={isTabbable ? 0 : -1}
      role="checkbox"
      aria-label={props['aria-label']}
      aria-checked={ariaChecked}
      onClick={props.onClick}>
      {props.checked ? (
        props.isPartial ? (
          <CheckboxMixedIcon />
        ) : (
          <CheckboxOnIcon />
        )
      ) : (
        <CheckboxOffIcon />
      )}
    </div>
  );
}

// click event handler used by Selector
const handleClick = (event: Event) => {
  // we don't want container component like ListView to process it
  event.stopPropagation();
};

// update selectionProps onClick event to call event.stopPropagation()
const updateSelectionProps = (selectionProps: any) => {
  // click event handler return from useSelection
  const onClick = selectionProps.onClick;
  if (onClick) {
    // click event handler used by Selector
    selectionProps.onClick = (event: MouseEvent) => {
      onClick(event);
      handleClick(event);
    };
    return selectionProps;
  }
  return { onClick: handleClick };
};

/**
 * The Selector component renders checkboxes in collections to support selection.
 */
export function Selector<K>({
  'aria-label': ariaLabel,
  rowKey,
  selectedKeys,
  isPartial,
  selectionMode = 'multiple',
  onChange
}: Props<K>) {
  const { selectionProps } = useSelection(
    () => rowKey,
    selectedKeys,
    selectionMode,
    false,
    'toggle',
    false,
    onChange
  );

  // TODO: replace internal with Preact checkbox component once it is available
  return (
    <div class={styles.container} {...updateSelectionProps(selectionProps)}>
      <div class={styles.base}>
        <Flex align="center" justify="center" width="11x" height="11x">
          <Checkbox
            checked={containsKey(selectedKeys, rowKey)}
            aria-label={ariaLabel}
            isPartial={isPartial}
          />
        </Flex>
      </div>
    </div>
  );
}
